import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { ArrowLeftIcon } from '@phosphor-icons/react';

import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import useLoading from '@/components/loading/hooks/useLoading';
import Connections, { ApiEndpoints } from '@/components/connections/Connections';
import CompanyForm from './CompanyForm';
import CompanySettings from './CompanySettings';
import CustomerList from '@/screens/customers/CustomerList';
import InvoiceList from '@/screens/invoices/InvoiceList';

const iconBtn = 'inline-flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors cursor-pointer';

const VALID_TABS = ['invoices', 'customers', 'info', 'settings'];

const CompanyDetail = () => {
  const { companyId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { loading, Loading, setLoading } = useLoading(true);
  const [company, setCompany] = useState(null);

  const tabFromUrl = searchParams.get('tab');
  const activeTab = VALID_TABS.includes(tabFromUrl) ? tabFromUrl : 'invoices';

  useEffect(() => {
    const load = async () => {
      const res = await Connections.postRequest(ApiEndpoints.companyGet, { companyId });
      if (res.ok) setCompany(res.data.company);
      setLoading(false);
    };
    load().then();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const handleTabChange = value => {
    setSearchParams({ tab: value }, { replace: true });
  };

  if (loading) return <Loading />;
  if (!company) return null;

  return (
    <>
      <Helmet>
        <title>{t('app.title')} | {company.name}</title>
      </Helmet>

      <div className="flex items-center gap-2 mb-6">
        <button type="button" className={iconBtn} onClick={() => navigate('/companies')}>
          <ArrowLeftIcon size={20} />
        </button>
        <h1 className="text-2xl font-semibold">{company.name}</h1>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="invoices">{t('invoices')}</TabsTrigger>
          <TabsTrigger value="customers">{t('customers')}</TabsTrigger>
          <TabsTrigger value="info">{t('company.info')}</TabsTrigger>
          <TabsTrigger value="settings">{t('settings')}</TabsTrigger>
        </TabsList>

        <TabsContent value="invoices">
          <InvoiceList companyId={companyId} embedded />
        </TabsContent>
        <TabsContent value="customers">
          <CustomerList companyId={companyId} embedded />
        </TabsContent>
        <TabsContent value="info">
          <CompanyForm companyId={companyId} embedded />
        </TabsContent>
        <TabsContent value="settings">
          <CompanySettings companyId={companyId} embedded />
        </TabsContent>
      </Tabs>
    </>
  );
};

export default CompanyDetail;
